import { useApolloClient } from "@apollo/client";
import { filterNonNull } from "@msys/common";
import { pick, uniq } from "lodash-es";
import React from "react";
import { ItemType } from "../../../clients/graphqlTypes.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { isAllowedToPaste } from "../doc-items/constraints.js";
import { useCreateItemRecommendationsLazyQuery } from "../doc-items/CreateItemInput.generated.js";
import { type CreateItemRecommendation } from "../doc-items/createItemRecommendations.js";
import {
  CreateItemFromTemplateArgs,
  CreateItemsFromTemplatesArgs,
  type CreateItemsReturn,
  type CreateItemsVariables,
  CreateItemsWithProductsArgs,
  useCreateItem,
} from "../doc-items/hooks/useCreateItem.js";
import { useDocItemsClipboard } from "../doc-items/hooks/useDocItemsClipboard.js";
import { useAvailabilityWarning } from "./alerts/QuoteProductsAvailabilityAlert.js";
import {
  useCreateQuoteItems_CreateItemFromItemRecommendationMutation,
  useCreateQuoteItems_CreateItemsFromTemplatesMutation,
  useCreateQuoteItems_CreateItemsMutation,
  useCreateQuoteItems_PasteItemFromClipboardMutation,
} from "./useCreateQuoteItems.generated.js";

export function useCreateQuoteItems({
  projectId,
  quoteId,
  expandedItemIds,
  onItemCreated,
  source,
}: {
  projectId: string;
  quoteId: string;
  expandedItemIds: string[] | undefined;
  onItemCreated?(newItem?: { id: string }): Promise<void> | void; // should be memoized
  source: "edit" | "calculation" | "3d" | "grid";
}) {
  const doProductAvailabilityWarning = useAvailabilityWarning();

  const client = useApolloClient();
  const [createItems] = useCreateQuoteItems_CreateItemsMutation({ client });

  const createDocumentItems = React.useCallback(
    async (args: {
      variables: CreateItemsVariables;
    }): Promise<CreateItemsReturn> => {
      return await createItems({
        ...args,
        variables: {
          ...args.variables,
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
    },
    [createItems, source]
  );

  const { createItem, createItemByType } = useCreateItem({
    projectId,
    createDocumentItems,
    expandedItemIds,
    onItemCreated,
  });

  const [createItemsFromTemplatesMutation] =
    useCreateQuoteItems_CreateItemsFromTemplatesMutation({
      client,
    });

  const createItemFromTemplate = React.useCallback(
    async ({
      parentItemId,
      templateQuoteId,
      newItemRank,
    }: CreateItemFromTemplateArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: quoteId,
            parentItemId,
            templates: [{ templateQuoteId }],
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      const newItem = result.data?.createItemsFromTemplates.items[0];
      await onItemCreated?.(newItem ?? undefined);
      return newItem;
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      quoteId,
      source,
    ]
  );

  const createItemsFromTemplates = React.useCallback(
    async ({
      parentItemId,
      templates,
      newItemRank,
    }: CreateItemsFromTemplatesArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: quoteId,
            parentItemId,
            templates: templates.map(tpl => ({
              templateQuoteId: tpl.template.id,
              templateEstimatedQuantity: tpl.quantity,
            })),
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      const newItems = result.data?.createItemsFromTemplates.items;
      await onItemCreated?.(newItems?.[0] ?? undefined);
      return newItems;
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      quoteId,
      source,
    ]
  );

  const createItemsWithProducts = React.useCallback(
    async ({
      parentItemId,
      products,
      newItemRank,
    }: CreateItemsWithProductsArgs) => {
      const result = await createItems({
        variables: {
          input: {
            projectId,
            docId: quoteId,
            parentItemId,
            items: products.map(opts =>
              "__typename" in opts.product
                ? {
                    title: opts.product.texts?.title ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    product: pick(opts.product, "supplierId", "articleNumber"),
                  }
                : {
                    title: opts.product.productTitle ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    productValues: opts.product,
                  }
            ),
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });

      const setProducts = filterNonNull(
        products.map(p => ("__typename" in p.product ? p.product : null))
      );

      const setProductsStatusWithAvailabilityWarning = uniq(
        filterNonNull(
          setProducts.map(p =>
            p.status && p.status.value !== "available" ? p.status.value : null
          )
        )
      );

      for (const status of setProductsStatusWithAvailabilityWarning) {
        doProductAvailabilityWarning(status);
      }

      const newItems = result.data?.createItems.items;
      await onItemCreated?.(newItems?.[0] ?? undefined);

      return newItems;
    },
    [
      createItems,
      projectId,
      quoteId,
      expandedItemIds,
      source,
      onItemCreated,
      doProductAvailabilityWarning,
    ]
  );

  const [copiedItem] = useDocItemsClipboard();
  const [pasteItemFromClipboardMutation] =
    useCreateQuoteItems_PasteItemFromClipboardMutation({ client });
  const pasteItem = React.useCallback(
    async (docId: string, parentItemId: string, rank?: number | null) => {
      if (!copiedItem) throw new Error("No item copied");
      const result = await pasteItemFromClipboardMutation({
        variables: {
          input: {
            sourceProjectId: copiedItem.projectId,
            sourceDocId: copiedItem.docId,
            sourceItemId: copiedItem.itemId,
            destProjectId: projectId,
            destDocId: docId,
            destItemId: parentItemId,
            rank: rank ?? null,
            expandedItemIds,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      // the inserted subtree is returned DFS, so items[0] is the pasted item
      const insertedItem = result.data?.pasteItem.items[0];
      await onItemCreated?.(insertedItem ?? undefined);
    },
    [
      copiedItem,
      pasteItemFromClipboardMutation,
      projectId,
      expandedItemIds,
      source,
      onItemCreated,
    ]
  );
  const isAllowedToPasteItem = React.useCallback(
    (parentItem: { type: ItemType }) => {
      if (!copiedItem?.itemType || !parentItem.type) return false;

      return isAllowedToPaste({
        documentItemTypes: QUOTE_ITEM_TYPES,
        copiedItemType: copiedItem.itemType,
        parentItemType: parentItem.type,
      });
    },
    [copiedItem?.itemType]
  );

  const [fetchRecommendationsQuery] = useCreateItemRecommendationsLazyQuery({
    client,
    fetchPolicy: "no-cache",
  });
  const fetchItemRecommendations = React.useCallback(
    async (itemId: string): Promise<CreateItemRecommendation[]> => {
      const result = await fetchRecommendationsQuery({
        variables: { projectId, docId: quoteId, itemId },
      });
      // Apollo's useLazyQuery execute promise resolves (not rejects) on
      // failure, so surface the error explicitly for callers to catch.
      if (result.error) throw result.error;
      const resultItem = result.data?.item;
      if (resultItem?.__typename !== "Item") return [];
      // Only template recommendations are surfaced in the combobox; template-type
      // recommendations are filtered out. Carry the exact template id + version
      // so the created item uses the recommended version.
      return filterNonNull(
        resultItem.recommendations.map(recommendation => {
          const entity = recommendation.recommendedEntity;
          if (entity.__typename !== "RecommendedTemplate") return null;
          return {
            id: recommendation.id,
            teaserTitle: recommendation.teaserTitle,
            rank: recommendation.rank,
            templateId: entity.templateId,
            templateVersionNumber: entity.templateVersionNumber,
          };
        })
      );
    },
    [fetchRecommendationsQuery, projectId, quoteId]
  );

  const [createItemFromItemRecommendationMutation] =
    useCreateQuoteItems_CreateItemFromItemRecommendationMutation({ client });
  const createItemFromRecommendation = React.useCallback(
    async ({
      itemId,
      itemRecommendationId,
    }: {
      itemId: string;
      itemRecommendationId: string;
    }) => {
      const result = await createItemFromItemRecommendationMutation({
        variables: {
          input: {
            projectId,
            docId: quoteId,
            itemId,
            itemRecommendationId,
            expandedItemIds,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      const newItem =
        result.data?.createItemFromItemRecommendation.createdItems[0];
      await onItemCreated?.(newItem ?? undefined);
    },
    [
      createItemFromItemRecommendationMutation,
      projectId,
      quoteId,
      expandedItemIds,
      source,
      onItemCreated,
    ]
  );

  return {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
    fetchItemRecommendations,
    createItemFromRecommendation,
  };
}
